import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/hooks/useSession';
import {
  getBookmarksCount,
  getAyahReadDetails,
  getQuranJuzIndex,
  getReadingProgress,
  getReadingStreak,
  getReadsByAllSurahs,
  getTotalAyahReads,
  type AyahReadDetail,
  type JuzIndexRow,
  type ReadingProgress,
  type StreakInfo,
} from '@/lib/queries/studentStats';

/**
 * Student-stats hooks.
 *
 * All gate on `useSession().data` so anonymous users skip the network
 * entirely. The query keys include the user_id so two users in the
 * same browser session don't see each other's cached counts (the
 * `USER_SCOPED_QUERY_KEYS` purge in main.tsx is belt-and-suspenders
 * on top of this).
 *
 * Cache config:
 *   - staleTime 60s — these numbers change rarely (only when the user
 *     marks ayahs or bookmarks). 60s avoids re-querying on every nav.
 *   - refetchOnWindowFocus true — when the user comes back to the tab,
 *     we want fresh numbers. Cheap COUNT queries.
 *
 * Mutations elsewhere (toggleAyahRead, bookmark add/remove) should
 * invalidate the matching key to force a recount. As a fallback the
 * SIGNED_IN / USER_UPDATED listener in main.tsx will invalidate them
 * on auth events.
 */

export function useTotalAyahReads() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery<number, Error>({
    queryKey: ['ayah-reads-total', userId],
    queryFn: getTotalAyahReads,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useReadingProgress() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery<ReadingProgress | null, Error>({
    queryKey: ['reading-progress', userId],
    queryFn: getReadingProgress,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useReadsByAllSurahs() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery<Map<number, number>, Error>({
    queryKey: ['ayah-reads-by-surah', userId],
    queryFn: getReadsByAllSurahs,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useReadingStreak() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery<StreakInfo, Error>({
    queryKey: ['reading-streak', userId],
    queryFn: getReadingStreak,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useBookmarksCount() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery<number, Error>({
    queryKey: ['bookmarks-count', userId],
    queryFn: getBookmarksCount,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useAyahReadDetails() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  return useQuery<AyahReadDetail[], Error>({
    queryKey: ['ayah-read-details', userId],
    queryFn: getAyahReadDetails,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useQuranJuzIndex() {
  return useQuery<JuzIndexRow[], Error>({
    queryKey: ['quran-juz-index'],
    queryFn: getQuranJuzIndex,
    staleTime: 24 * 60 * 60_000,
  });
}
